'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { getApiBaseUrl } from '@/lib/config'
import { getImageUrl, getMediaFileUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import { extractYoutubeVideoId } from '@/lib/comment-embed-pipeline'
import { extractDailymotionVideoId, extractVimeoVideoId } from '@/lib/video-utils'
import { rewriteContentImageUrlsForDisplay } from '@/lib/process-article-content-images'
import { sanitizeArticleContentHtml } from '@/lib/sanitize-article-content-html'
import { sanitizePublicArticleHtml } from '@/lib/safe-html'
import { useNativeProviderEmbeds } from '@/lib/use-native-provider-embeds'
import { LiveBeaconIcon } from '@/components/LiveBadge'
import VideoPlayer from '@/components/VideoPlayer'
import { formatSiteDate, formatSiteTime } from '@/lib/site-datetime'

export type LiveUpdate = {
  id: number
  title?: string | null
  body: string
  image?: string | null
  video?: string | null
  createdAt: string
}

const INITIAL_VISIBLE = 10

function stripHtml(html: string) {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function looksLikeHtml(value: string) {
  return /<\/?[a-z][\s\S]*>/i.test(value)
}

function prepareLiveBodyHtml(body: string) {
  const trimmed = body.trim()
  if (!trimmed) return ''
  if (!looksLikeHtml(trimmed)) {
    return trimmed
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => `<p>${line.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`)
      .join('')
  }
  return sanitizePublicArticleHtml(
    rewriteContentImageUrlsForDisplay(sanitizeArticleContentHtml(trimmed)),
  )
}

function listSignature(list: LiveUpdate[]) {
  return `${list.length}:${list[0]?.id ?? 0}:${list[0]?.createdAt ?? ''}:${list[0]?.title ?? ''}`
}

function formatClock(iso: string) {
  try {
    return formatSiteTime(iso, {
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

function isSameDay(a: string, b: string) {
  return (
    formatSiteDate(a, { year: 'numeric', month: '2-digit', day: '2-digit' }) ===
    formatSiteDate(b, { year: 'numeric', month: '2-digit', day: '2-digit' })
  )
}

function formatDayLabel(iso: string) {
  try {
    return formatSiteDate(iso, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
  } catch {
    return ''
  }
}

function formatRelative(iso: string, nowMs: number) {
  const diff = Math.max(0, nowMs - new Date(iso).getTime())
  const sec = Math.floor(diff / 1000)
  if (sec < 45) return 'Az önce'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} dk önce`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} sa önce`
  return `${Math.floor(hr / 24)} gün önce`
}

function LiveVideoEmbed({ video }: { video: string }) {
  const yt = extractYoutubeVideoId(video)
  const dm = extractDailymotionVideoId(video)
  const vimeo = extractVimeoVideoId(video)
  const src = /^https?:\/\//i.test(video) ? video : getMediaFileUrl(video)
  if (!yt && !dm && !vimeo && !src) return null

  return (
    <div className="live-blog__media live-blog__media--video">
      <VideoPlayer
        video={!yt && !dm && !vimeo ? src : null}
        youtube={yt ? video : null}
        dailymotion={dm ? video : null}
        vimeo={vimeo ? video : null}
        title="Canlı blog video"
        className="!mb-0 !rounded-[5px] !shadow-none"
      />
    </div>
  )
}

function LiveBodyHtml({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useNativeProviderEmbeds(ref, html)

  return (
    <div
      ref={ref}
      className="live-blog__text news-content"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

export default function LiveBlogUpdates({
  newsId,
  initialUpdates = [],
}: {
  newsId: number
  initialUpdates?: LiveUpdate[]
}) {
  const [updates, setUpdates] = useState<LiveUpdate[]>(initialUpdates)
  const [loading, setLoading] = useState(initialUpdates.length === 0)
  const [expanded, setExpanded] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const [freshIds, setFreshIds] = useState<Set<number>>(() => new Set())
  const knownIdsRef = useRef<Set<number>>(new Set(initialUpdates.map((u) => u.id)))
  const lastSigRef = useRef(listSignature(initialUpdates))

  const applyList = useCallback((list: LiveUpdate[]) => {
    const sig = listSignature(list)
    if (sig === lastSigRef.current) return
    lastSigRef.current = sig

    const prev = knownIdsRef.current
    const arrived = list.filter((u) => !prev.has(u.id)).map((u) => u.id)
    if (arrived.length && prev.size > 0) {
      setFreshIds(new Set(arrived))
      window.setTimeout(() => setFreshIds(new Set()), 10_000)
    }
    knownIdsRef.current = new Set(list.map((u) => u.id))
    setUpdates(list)
    setLoading(false)
  }, [])

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/news/${newsId}/live-updates`, {
        cache: 'no-store',
      })
      if (!res.ok) return
      const json = await res.json()
      const list: LiveUpdate[] = Array.isArray(json?.data) ? json.data : []
      applyList(list)
    } catch {
      /* keep last */
    } finally {
      setLoading(false)
    }
  }, [newsId, applyList])

  useEffect(() => {
    knownIdsRef.current = new Set(initialUpdates.map((u) => u.id))
    lastSigRef.current = listSignature(initialUpdates)
    setUpdates(initialUpdates)
    setLoading(initialUpdates.length === 0)
    setExpanded(false)
    setFreshIds(new Set())
  }, [newsId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false
    let pollTimer: number | null = null
    let es: EventSource | null = null

    const startPoll = (ms: number) => {
      if (pollTimer) window.clearInterval(pollTimer)
      pollTimer = window.setInterval(() => {
        if (document.visibilityState === 'hidden') return
        void load()
      }, ms)
    }

    void load()

    try {
      es = new EventSource(`${getApiBaseUrl()}/api/news/${newsId}/live-updates/stream`)
      es.onmessage = (ev) => {
        if (cancelled) return
        try {
          const json = JSON.parse(ev.data) as { data?: LiveUpdate[] }
          if (Array.isArray(json?.data)) applyList(json.data)
          setLoading(false)
        } catch {
          /* ignore bad frame */
        }
      }
      es.onerror = () => {
        // Fallback poll when SSE drops (proxy / CORS / SSL)
        es?.close()
        es = null
        if (!cancelled) startPoll(4000)
      }
      // Light backup poll even with SSE (missed frames)
      startPoll(12_000)
    } catch {
      startPoll(4000)
    }

    const onVis = () => {
      if (document.visibilityState === 'visible') void load()
    }
    document.addEventListener('visibilitychange', onVis)

    const tick = window.setInterval(() => setNowMs(Date.now()), 20_000)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVis)
      if (pollTimer) window.clearInterval(pollTimer)
      window.clearInterval(tick)
      es?.close()
    }
  }, [newsId, load, applyList])

  const latestAt = updates[0]?.createdAt
  const visible = useMemo(
    () => (expanded ? updates : updates.slice(0, INITIAL_VISIBLE)),
    [expanded, updates],
  )
  const hiddenCount = Math.max(0, updates.length - INITIAL_VISIBLE)

  const spansMultipleDays = useMemo(() => {
    if (updates.length < 2) return false
    const first = updates[0]?.createdAt
    return updates.some((u) => first && !isSameDay(first, u.createdAt))
  }, [updates])

  const rows = useMemo(() => {
    const out: Array<
      | { type: 'day'; key: string; label: string }
      | { type: 'item'; key: string; update: LiveUpdate; index: number }
    > = []
    visible.forEach((u, index) => {
      const prev = visible[index - 1]
      if (spansMultipleDays && (!prev || !isSameDay(prev.createdAt, u.createdAt))) {
        out.push({ type: 'day', key: `d-${u.createdAt.slice(0, 10)}`, label: formatDayLabel(u.createdAt) })
      }
      out.push({ type: 'item', key: `u-${u.id}`, update: u, index })
    })
    return out
  }, [visible, spansMultipleDays])

  return (
    <section className="live-blog" aria-label="Canlı gelişmeler">
      <header className="live-blog__head">
        <div className="live-blog__head-main">
          <h2 className="live-blog__live-pill">
            <LiveBeaconIcon />
            Son Gelişmeler
          </h2>
        </div>
        <p className="live-blog__status">
          {loading && updates.length === 0
            ? 'Yükleniyor…'
            : latestAt
              ? formatRelative(latestAt, nowMs)
              : 'Bekleniyor'}
        </p>
      </header>

      {updates.length === 0 && !loading ? (
        <p className="live-blog__empty">
          Henüz güncelleme yok. Bu sayfa açık kaldıkça yeni gelişmeler burada görünür.
        </p>
      ) : (
        <>
          <ol className="live-blog__list">
            {rows.map((row) => {
              if (row.type === 'day') {
                return (
                  <li key={row.key} className="live-blog__day">
                    <span>{row.label}</span>
                  </li>
                )
              }
              const u = row.update
              const isFresh = freshIds.has(u.id)
              const isLatest = row.index === 0
              const imageUrl = u.image ? getImageUrl(u.image, IMAGE_PRESETS.detail) : null
              const bodyPlain = stripHtml(u.body || '')
              const showBody = Boolean(bodyPlain) && bodyPlain !== (u.title || '').trim()
              const bodyHtml = showBody ? prepareLiveBodyHtml(u.body || '') : ''

              return (
                <li
                  key={row.key}
                  className={[
                    'live-blog__post',
                    isLatest ? 'live-blog__post--latest' : '',
                    isFresh ? 'live-blog__post--fresh' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <time dateTime={u.createdAt} className="live-blog__time">
                    {formatClock(u.createdAt)}
                  </time>
                  <div className="live-blog__content">
                    {isFresh ? <span className="live-blog__chip">Yeni</span> : null}
                    {u.title?.trim() ? (
                      <h3 className="live-blog__post-title">{u.title.trim()}</h3>
                    ) : null}
                    {bodyHtml ? <LiveBodyHtml html={bodyHtml} /> : null}
                    {imageUrl ? (
                      <div className="live-blog__media">
                        <Image
                          src={imageUrl}
                          alt={u.title?.trim() || 'Canlı güncelleme görseli'}
                          width={960}
                          height={540}
                          className="live-blog__img"
                          sizes="(max-width: 768px) 100vw, 720px"
                        />
                      </div>
                    ) : null}
                    {u.video?.trim() ? <LiveVideoEmbed video={u.video.trim()} /> : null}
                  </div>
                </li>
              )
            })}
          </ol>

          {hiddenCount > 0 ? (
            <button
              type="button"
              className="live-blog__more"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Daha az göster' : `Daha eski ${hiddenCount} gelişmeyi göster`}
            </button>
          ) : null}
        </>
      )}
    </section>
  )
}
